import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import confetti from 'canvas-confetti';
import chinaImage from '@/china.jpg';

// Компонент для снежинок
function Snowflake({ delay, duration, left }: { delay: number; duration: number; left: number }) {
  return (
    <div
      className="absolute text-white text-2xl opacity-70 pointer-events-none"
      style={{
        left: `${left}%`,
        animation: `fall ${duration}s linear ${delay}s infinite`,
        top: '-50px',
      }}
    >
      ❄
    </div>
  );
}

interface Prize {
  id: number;
  text: string;
  color: string;
  bgColor: string;
}

const LotteryWheel: React.FC = () => {
  const navigate = useNavigate();
  
  // Награды для каждого сектора
  const prizes: Prize[] = [
    { id: 1, text: '10% скидка', color: '#FFFFFF', bgColor: '#EF4444' },
    {
      id: 2,
      text: 'Подписка Яндекс Плюс',
      color: '#000000',
      bgColor: '#F59E0B',
    },
    { id: 3, text: 'Подарок', color: '#FFFFFF', bgColor: '#10B981' },
    { id: 4, text: '1000 баллов', color: '#000000', bgColor: '#3B82F6' },
    { id: 5, text: '25% скидка', color: '#FFFFFF', bgColor: '#8B5CF6' },
    { id: 6, text: 'Билет в кино', color: '#000000', bgColor: '#EC4899' },
    { id: 7, text: 'Подарочный сертификат', color: '#FFFFFF', bgColor: '#6366F1' },
    { id: 8, text: 'Супер-приз', color: '#000000', bgColor: '#14B8A6' },
  ];

  const [isSpinning, setIsSpinning] = useState<boolean>(false);
  const [result, setResult] = useState<Prize | null>(null);
  const [rotation, setRotation] = useState<number>(0);
  const [_spins, setSpins] = useState<number>(0);
  const [showSuperPrizeModal, setShowSuperPrizeModal] = useState<boolean>(false);
  const [isSpinned, seIsSpinned] = useState<boolean>(false);
  const [isOpeningGift, setIsOpeningGift] = useState<boolean>(false);
  const [giftOpened, setGiftOpened] = useState<boolean>(false);
  const wheelRef = useRef<HTMLDivElement>(null);

  // Генерируем снежинки один раз при монтировании компонента
  const snowflakes = useMemo(() => {
    return Array.from({ length: 10 }, () => ({
      delay: Math.random() * 5,
      duration: 5 + Math.random() * 10,
      left: Math.random() * 100,
    }));
  }, []);

  // Генерируем звезды один раз при монтировании компонента
  const stars = useMemo(() => {
    return Array.from({ length: 100 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      width: Math.random() * 3 + 1,
      height: Math.random() * 3 + 1,
      delay: Math.random() * 3,
      duration: 2 + Math.random() * 2,
    }));
  }, []);

  // Настройки колеса
  const totalSegments = prizes.length;
  const segmentAngle = 360 / totalSegments;
  
  // Responsive wheel size
  const [wheelSize, setWheelSize] = useState<number>(550);
  
  // Calculate wheel size based on screen width
  useEffect(() => {
    const updateWheelSize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        // Mobile: smaller wheel
        setWheelSize(Math.min(340, width - 20));
      } else if (width < 768) {
        // Small tablets
        setWheelSize(400);
      } else {
        // Desktop
        setWheelSize(550);
      }
    };
    
    updateWheelSize();
    window.addEventListener('resize', updateWheelSize);
    return () => window.removeEventListener('resize', updateWheelSize);
  }, []);

  // Запуск вращения колеса
  const spinWheel = () => {
    if (isSpinning) return;

    setIsSpinning(true);
    setResult(null);
    setShowSuperPrizeModal(false);

    // Генерируем случайное количество оборотов (3-5 полных оборотов)
    const fullRotations = 10 + Math.floor(Math.random() * 3); // 3-5 полных оборотов

    // Выбираем случайный сектор для выигрыша
    const winningSegmentIndex = 7

    // Вычисляем конечный угол так, чтобы стрелка указывала на центр выигрышного сектора
    // Для этого поворачиваем колесо так, чтобы центр нужного сектора оказался под стрелкой
    // Стрелка указывает на 0 градусов, поэтому центр сектора должен быть на 0 градусов
    // Центр сектора находится на угле: segmentAngle * winningSegmentIndex + segmentAngle/2
    // Чтобы этот центр оказался на 0 градусов, нужно повернуть колесо на противоположный угол
    const targetCenterAngle =
      segmentAngle * winningSegmentIndex + segmentAngle / 1.08;
    const finalRotation = 360 * fullRotations + (360 - targetCenterAngle);

    // Анимация вращения
    const spinDuration = 6000 + fullRotations * 1000; // Длительность анимации

    if (wheelRef.current) {
      wheelRef.current.style.transition = `transform ${spinDuration}ms cubic-bezier(0.2, 0.8, 0.3, 1)`;
      wheelRef.current.style.transform = `rotate(${finalRotation}deg)`;
    }

    // Обновляем состояние вращения
    setRotation(finalRotation);

    // Определяем результат после завершения анимации
    setTimeout(() => {
      const winningPrize = prizes[winningSegmentIndex];
      setResult(winningPrize);
      setIsSpinning(false);
      setSpins(prev => prev + 1);
      seIsSpinned(true)

      // Визуально подсветим выигрышный сектор
      highlightWinningSegment(winningSegmentIndex);

      // Запускаем конфетти при победе
      triggerConfetti();

      // Показываем модальное окно через 3 секунды после остановки
      setTimeout(() => {
        setShowSuperPrizeModal(true);
        setGiftOpened(false);
        setIsOpeningGift(false);
        // Еще один взрыв конфетти при открытии модального окна
        triggerConfetti();
      }, 3000);
    }, spinDuration); 
  };

  // Функция для запуска конфетти
  const triggerConfetti = () => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 10000 };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const interval: NodeJS.Timeout = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      
      // Конфетти слева
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      
      // Конфетти справа
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);

    // Финальный взрыв
    setTimeout(() => {
      confetti({
        ...defaults,
        particleCount: 100,
        origin: { x: 0.5, y: 0.5 },
        colors: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8']
      });
    }, duration - 500);
  };

  // Подсветка выигрышного сектора
  const highlightWinningSegment = (segmentIndex: number) => {
    const sectors = document.querySelectorAll('.wheel-sector');
    sectors.forEach((sector, index) => {
      if (index === segmentIndex) {
        // Подсвечиваем сам сектор
        (sector as HTMLElement).style.filter =
          'brightness(1.3) drop-shadow(0 0 10px rgba(255,255,255,0.7))';
        
        // Подсвечиваем левую границу выигрышного сектора
        const leftBorder = (sector as HTMLElement).querySelector('.sector-border-left');
        if (leftBorder) {
          (leftBorder as HTMLElement).style.backgroundColor = 'rgba(255, 255, 0, 1)';
          (leftBorder as HTMLElement).style.boxShadow = '0 0 15px rgba(255, 255, 0, 0.9), 0 0 25px rgba(255, 255, 0, 0.6)';
          (leftBorder as HTMLElement).style.zIndex = '40';
        }
        
        // Подсвечиваем правую границу выигрышного сектора
        const rightBorder = (sector as HTMLElement).querySelector('.sector-border-right');
        if (rightBorder) {
          (rightBorder as HTMLElement).style.backgroundColor = 'rgba(255, 255, 0, 1)';
          (rightBorder as HTMLElement).style.boxShadow = '0 0 15px rgba(255, 255, 0, 0.9), 0 0 25px rgba(255, 255, 0, 0.6)';
          (rightBorder as HTMLElement).style.zIndex = '40';
        }
        
        // Также подсвечиваем левую границу следующего сектора (которая визуально совпадает с правой границей текущего)
        // Это гарантирует, что правая граница видна даже если она обрезана clipPath
        const nextSegmentIndex = (segmentIndex + 1) % totalSegments;
        const nextSector = sectors[nextSegmentIndex] as HTMLElement;
        if (nextSector) {
          const nextLeftBorder = nextSector.querySelector('.sector-border-left');
          if (nextLeftBorder) {
            (nextLeftBorder as HTMLElement).style.backgroundColor = 'rgba(255, 255, 0, 1)';
            (nextLeftBorder as HTMLElement).style.boxShadow = '0 0 15px rgba(255, 255, 0, 0.9), 0 0 25px rgba(255, 255, 0, 0.6)';
            (nextLeftBorder as HTMLElement).style.zIndex = '40';
          }
        }
      } else {
        (sector as HTMLElement).style.filter = 'brightness(0.7)';
      }
    });

    // Сбрасываем подсветку через 3 секунды
    setTimeout(() => {
      sectors.forEach((sector) => {
        (sector as HTMLElement).style.filter = '';
        
        // Сбрасываем подсветку границ
        const leftBorder = (sector as HTMLElement).querySelector('.sector-border-left');
        const rightBorder = (sector as HTMLElement).querySelector('.sector-border-right');
        
        if (leftBorder) {
          (leftBorder as HTMLElement).style.backgroundColor = 'rgba(255, 255, 255, 0.7)';
          (leftBorder as HTMLElement).style.boxShadow = '0 0 5px rgba(255,255,255,0.5)';
          (leftBorder as HTMLElement).style.zIndex = '';
        }
        
        if (rightBorder) {
          (rightBorder as HTMLElement).style.backgroundColor = 'rgba(255, 255, 255, 0.7)';
          (rightBorder as HTMLElement).style.boxShadow = '0 0 5px rgba(255,255,255,0.5)';
          (rightBorder as HTMLElement).style.zIndex = '';
        }
      });
    }, 3000);
  };

  // Инициализация колеса при первом рендере
  useEffect(() => {
    if (wheelRef.current) {
      wheelRef.current.style.transform = `rotate(0deg)`;
    }
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-slate-900 via-blue-900 to-indigo-900 dark:from-gray-950 dark:via-blue-950 dark:to-indigo-950">
      {/* Новогодний фон с звездами */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {stars.map((star) => (
          <div
            key={star.id}
            className="absolute rounded-full bg-white opacity-60 animate-twinkle"
            style={{
              left: `${star.left}%`,
              top: `${star.top}%`,
              width: `${star.width}px`,
              height: `${star.height}px`,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`,
            }}
          />
        ))}
      </div>

      {/* Анимация падающего снега */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-10">
        {snowflakes.map((flake, i) => (
          <Snowflake key={i} delay={flake.delay} duration={flake.duration} left={flake.left} />
        ))}
      </div>

      {/* CSS для анимации снега и подарка */}
      <style>{`
        @keyframes fall {
          to {
            transform: translateY(100vh) rotate(360deg);
          }
        }
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-twinkle {
          animation: twinkle 2s ease-in-out infinite;
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>

      {/* Шапка сайта в стиле Kukmara */}
      <header className="relative z-30 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Логотип */}
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-white rounded-lg shadow-md border border-gray-200">
                <span className="text-red-600 font-bold text-4xl md:text-xl">K</span>
              </div>
              <div className="flex flex-col">
                <span className="text-lg md:text-xl font-bold text-red-600 dark:text-red-500 leading-tight">
                  KUKMARA
                </span>
                <span className="text-xs text-gray-600 dark:text-gray-400 leading-tight">
                  Колесо Фортуны
                </span>
              </div>
            </div>

            {/* Навигация */}
            <nav className="hidden md:flex items-center gap-6">
              <Link
                to="/"
                className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors"
              >
                Главная
              </Link>
              <Link
                to="/wheel"
                className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors"
              >
                Колесо
              </Link>
            </nav>

            {/* Контакты */}
            <div className="flex items-center gap-4">
              <div className="hidden lg:flex flex-col items-end">
                <span className="text-sm text-gray-600 dark:text-gray-400">Бесплатно по России</span>
                <a
                  href="tel:+78003339345"
                  className="text-lg font-bold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  +7 (800) 333-93-45
                </a>
              </div>
              <div className="lg:hidden">
                <a
                  href="tel:+78003339345"
                  className="text-base font-bold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  +7 (800) 333-93-45
                </a>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Контент с относительным позиционированием */}
      <div className="relative z-20 flex flex-col items-center justify-center min-h-screen text-white p-2 sm:p-4 pt-12 md:pt-12">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 text-yellow-400 text-center px-2">
          Лотерейное колесо
        </h1>
        <p className="text-gray-300 mb-4 sm:mb-6 md:mb-8 text-sm sm:text-base text-center px-2">Крутите колесо, чтобы выиграть приз!</p>

      <div className="relative w-full max-w-[420px] sm:max-w-none flex flex-col md:flex-row items-center justify-center pt-8 gap-4 sm:gap-6 md:gap-8 mx-auto">
        {/* Контейнер для колеса */}
        <div className="relative" style={{ width: `${wheelSize}px`, height: `${wheelSize}px` }}>
          {/* Яркая заметная стрелка с указателем на центр сектора */}
          <div 
            className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1 z-30"
            style={{
              width: `${wheelSize * 0.28}px`,
              height: `${wheelSize * 0.12}px`,
            }}
          >
            {/* Красная линия-указатель на центр сектора */}
            <div 
              className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-linear-to-b from-red-500 to-transparent z-10"
              style={{
                width: `${Math.max(1, wheelSize * 0.002)}px`,
                height: `${wheelSize * 0.15}px`,
              }}
            ></div>

            {/* Основание стрелки */}
            <div 
              className="absolute top-0.5 left-1/2 transform -translate-x-1/2 bg-linear-to-b from-red-800 to-red-900 rounded-b-lg shadow-inner"
              style={{
                width: `${wheelSize * 0.055}px`,
                height: `${wheelSize * 0.09}px`,
              }}
            ></div>

            {/* Тень под стрелкой для объемности */}
            <div 
              className="absolute top-1 left-1/2 transform -translate-x-1/2 bg-black/50 blur-md rounded-full"
              style={{
                width: `${wheelSize * 0.073}px`,
                height: `${wheelSize * 0.036}px`,
              }}
            ></div>

            {/* Точка в центре указателя */}
            <div 
              className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-yellow-400 rounded-full z-20 shadow-[0_0_10px_2px_rgba(255,255,0,0.7)]"
              style={{
                width: `${wheelSize * 0.027}px`,
                height: `${wheelSize * 0.027}px`,
              }}
            ></div>
          </div>

          {/* Колесо */}
          <div
            ref={wheelRef}
            className="relative rounded-full overflow-hidden shadow-2xl border-gray-800"
            style={{
              width: `${wheelSize}px`,
              height: `${wheelSize}px`,
              transform: `rotate(${rotation}deg)`,
              borderWidth: `${Math.max(4, wheelSize * 0.0145)}px`,
            }}
          >
            {/* Секторы колеса */}
            {prizes.map((prize, index) => {
              const angle = index * segmentAngle;
              const isEven = false; // index % 2 === 0;

              return (
                <div
                  key={prize.id}
                  className="wheel-sector absolute top-0 left-0 w-full h-full origin-center transition-all duration-500"
                  style={{
                    transform: `rotate(${angle}deg)`,
                  }}
                >
                  {/* Сектор круга */}
                  <svg
                    className="absolute top-0 left-0 w-full h-full origin-center"
                    style={{
                      transition: 'filter 0.5s ease',
                    }}
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                  >
                    <path
                      d={`M 50 50 L 50 0 A 50 50 0 ${segmentAngle > 180 ? 1 : 0} 1 ${50 + 50 * Math.cos((segmentAngle * Math.PI) / 180)} ${50 - 50 * Math.sin((segmentAngle * Math.PI) / 180)} Z`}
                      fill={prize.bgColor}
                    />
                  </svg>

                  {/* Левая граница сектора */}
                  <div
                    className="sector-border sector-border-left absolute top-0 left-1/2 w-1 h-1/2 origin-bottom"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                      boxShadow: '0 0 5px rgba(255,255,255,0.5)',
                      transform: 'translateX(-50%)',
                      transition: 'all 0.5s ease',
                      zIndex: 5,
                    }}
                  />
                  
                  {/* Правая граница сектора */}
                  <div
                    className="sector-border sector-border-right absolute top-0 left-1/2 w-1 h-1/2 origin-bottom"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                      boxShadow: '0 0 5px rgba(255,255,255,0.5)',
                      transform: `translateX(-50%) rotate(${segmentAngle}deg)`,
                      transition: 'all 0.5s ease',
                      zIndex: 5,
                    }}
                  />

                  {/* Текст награды - правильное позиционирование */}
                  <div
                    className="absolute text-center font-bold flex items-center justify-center"
                    style={{
                      // color: prize.color,
                      top: '15%',
                      left: '65%',
                      transform: `rotate(${segmentAngle / 2}deg) translateX(-50%)`,
                      width: `${wheelSize * 0.182}px`,
                      height: `${wheelSize * 0.073}px`,
                      fontSize: `${Math.max(8, wheelSize * 0.024)}px`,
                      textShadow: '2px 2px 3px rgba(0,0,0,0.9)',
                      transformOrigin: '0 0',
                    }}
                  >
                    <div
                      className="px-1 sm:px-2 py-0.5 sm:py-1 rounded-lg border border-white/30 backdrop-blur-sm"
                      style={{
                        backgroundColor: `${isEven ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'}`,
                        borderWidth: `${Math.max(1, wheelSize * 0.0036)}px`,
                      }}
                    >
                      {prize.text}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Центр колеса */}
            <div 
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gray-900 rounded-full z-10 border-yellow-500 flex items-center justify-center shadow-2xl"
              style={{
                width: `${wheelSize * 0.255}px`,
                height: `${wheelSize * 0.255}px`,
                borderWidth: `${Math.max(4, wheelSize * 0.0145)}px`,
              }}
            >
              <div className="text-yellow-400 text-center">
                <span 
                  className="block font-bold"
                  style={{ fontSize: `${Math.max(8, wheelSize * 0.025)}px` }}
                >
                  КРУТИ
                </span>
                <span 
                  className="block"
                  style={{ fontSize: `${Math.max(6, wheelSize * 0.018)}px` }}
                >
                  КОЛЕСО
                </span>
                <div 
                  className="bg-red-500 rounded-full mt-1 mx-auto shadow-[0_0_8px_2px_rgba(255,0,0,0.7)]"
                  style={{
                    width: `${wheelSize * 0.036}px`,
                    height: `${wheelSize * 0.036}px`,
                  }}
                ></div>
              </div>
            </div>

            {/* Ободок колеса с делениями */}
            <div 
              className="absolute top-0 left-0 w-full h-full rounded-full border-gray-900/50 pointer-events-none"
              style={{
                borderWidth: `${Math.max(2, wheelSize * 0.0073)}px`,
              }}
            >
              {/* Деления на ободке */}
              {Array.from({ length: totalSegments }).map((_, index) => {
                const angle = index * segmentAngle;
                return (
                  <div
                    key={index}
                    className="absolute top-0 left-1/2 h-1/2 origin-bottom"
                    style={{
                      transform: `rotate(${angle}deg)`,
                    }}
                  >
                    <div 
                      className="absolute bottom-0 left-1/2 bg-white/80 transform -translate-x-1/2"
                      style={{
                        width: `${Math.max(1, wheelSize * 0.0018)}px`,
                        height: `${wheelSize * 0.055}px`,
                      }}
                    ></div>
                  </div>
                );
              })}
            </div>
          </div>


        </div>

        {/* Панель управления и результатов */}
        <div className="bg-gray-800/70 backdrop-blur-sm rounded-xl p-4 sm:p-6 w-full max-w-md md:max-w-xl border border-gray-700 mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-center text-yellow-400">
            Управление
          </h2>

          <div className="space-y-3 sm:space-y-4">
            {isSpinned ? <p className="flex items-center justify-center">Вы уже испытали свою удачу!</p> :
            <button
              onClick={spinWheel}
              disabled={isSpinning}
              className={`w-full py-2.5 sm:py-3 px-4 rounded-lg font-bold text-base sm:text-lg transition-all ${
                isSpinning
                  ? 'bg-gray-600 cursor-not-allowed'
                  : 'bg-linear-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 active:scale-95'
              }`}
            >
              {isSpinning ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2"></div>
                  <span className="text-sm sm:text-base">Колесо крутится...</span>
                </div>
              ) : (
                'Крутить колесо!'
              )}
            </button>}

            {/* Результат */}
            {/* {result && (
              <div className="mt-4 sm:mt-6 p-3 sm:p-4 rounded-lg border-2 border-yellow-500 bg-linear-to-r from-gray-800 to-gray-900 animate-pulse">
                <h3 className="text-lg sm:text-xl font-bold mb-2 text-center text-yellow-400">
                  🎉 Поздравляем! 🎉
                </h3>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                  <div
                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-base sm:text-xl border-2 sm:border-4 border-white/40 shadow-2xl animate-bounce shrink-0"
                    style={{
                      backgroundColor: result.bgColor,
                      color: result.color,
                    }}
                  >
                    {result.id}
                  </div>
                  <div className="text-center sm:text-left">
                    <p className="text-base sm:text-lg font-bold">Вы выиграли:</p>
                    <p
                      className="text-xl sm:text-2xl md:text-3xl font-extrabold mt-1 break-words"
                      style={{ color: result.bgColor }}
                    >
                      {result.text}
                    </p>
                  </div>
                </div>
                <p className="text-gray-300 text-center mt-3 sm:mt-4 text-xs sm:text-sm">
                  Приз будет начислен в течение 24 часов
                </p>
              </div>
            )} */}

            {/* Правила */}
            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-900/50 rounded-lg">
              <h3 className="font-bold text-base sm:text-lg mb-2 text-yellow-400">
                Механика колеса:
              </h3>
              <ul className="text-xs sm:text-sm text-gray-300 space-y-1.5 sm:space-y-2">
                
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-yellow-500 rounded-full mt-1 mr-2 shrink-0"></div>
                  <span>
                    Колесо останавливается так, чтобы стрелка указывала на
                    выигрышный сектор
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full mt-1 mr-2 shrink-0"></div>
                  <span>
                    Выигрышный сектор подсвечивается после остановки колеса
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full mt-1 mr-2 shrink-0"></div>
                  <span>Границы между секторами обозначены белыми линиями</span>
                </li>
              </ul>
            </div>

            {/* Сектора колеса */}
            <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-900/30 rounded-lg">
              <h3 className="font-bold text-base sm:text-lg mb-2 sm:mb-3 text-yellow-400">
                Призы на колесе:
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2">
                {prizes.map(prize => (
                  <div
                    key={prize.id}
                    className="flex items-center p-1.5 sm:p-2 rounded-md hover:scale-105 transition-transform"
                    style={{ backgroundColor: `${prize.bgColor}40` }}
                  >
                    <div
                      className="w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold mr-2 border border-white/30 shrink-0"
                      style={{
                        backgroundColor: prize.bgColor,
                        color: prize.color,
                      }}
                    >
                      {prize.id}
                    </div>
                    <span className="text-xs sm:text-sm truncate">{prize.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

        {/* Футер */}
        <footer className="mt-6 sm:mt-8 md:mt-10 text-center py-8 border-t border-white/20 w-full">
          <p className="text-yellow-200 mb-2">
            © 2013-2026 KUKMARA
          </p>
          <p className="text-yellow-200">
            Контактный центр: +7 (800) 333-93-45
          </p>
        </footer>
      </div>

      {/* Модальное окно с поздравлением */}
      <Dialog open={showSuperPrizeModal} onOpenChange={setShowSuperPrizeModal}>
        <DialogContent 
          className="max-w-[calc(100%-2rem)] sm:max-w-[400px] md:max-w-[450px] bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-500 p-4 sm:p-6 md:p-8"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          {!giftOpened ? (
            <>
              <DialogHeader className="px-0 sm:px-2">
                <DialogTitle className="text-base sm:text-xl md:text-2xl lg:text-3xl font-bold text-center text-yellow-400 leading-tight break-words px-2">
                  Поздравляем! Вы выиграли Супер-приз
                </DialogTitle>
              </DialogHeader>
              <DialogDescription className="text-center text-white text-lg sm:text-xl md:text-2xl mt-3 sm:mt-4 md:mt-6 px-2">
                <div className="flex flex-col items-center justify-center min-h-[200px]">
                  {/* Анимация подарка */}
                  <div 
                    className={`relative transition-all duration-500 ${isOpeningGift ? 'scale-150 rotate-12 opacity-0' : 'scale-100 rotate-0 opacity-100'}`}
                    style={{ transformOrigin: 'center center' }}
                  >
                    <div className="text-8xl md:text-9xl animate-bounce">
                      🎁
                    </div>
                    {isOpeningGift && (
                      <>
                        {/* Конфетти при открытии */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-6xl animate-ping">✨</div>
                        </div>
                        <div className="absolute top-0 left-1/4 text-4xl animate-bounce" style={{ animationDelay: '0.1s' }}>🎉</div>
                        <div className="absolute top-0 right-1/4 text-4xl animate-bounce" style={{ animationDelay: '0.2s' }}>🎊</div>
                        <div className="absolute bottom-0 left-1/3 text-4xl animate-bounce" style={{ animationDelay: '0.3s' }}>⭐</div>
                        <div className="absolute bottom-0 right-1/3 text-4xl animate-bounce" style={{ animationDelay: '0.4s' }}>💫</div>
                      </>
                    )}
                  </div>
                  {!isOpeningGift && (
                    <p className="mt-4 text-yellow-300 text-sm md:text-base">
                      Нажмите кнопку, чтобы открыть подарок!
                    </p>
                  )}
                </div>
              </DialogDescription>
              <DialogFooter className="flex-col sm:flex-row justify-center mt-2 sm:mt-4 md:mt-6 gap-3 sm:gap-4 px-0 sm:px-2">
                {!isOpeningGift ? (
                  <Button
                    onClick={() => {
                      setIsOpeningGift(true);
                      // Запускаем конфетти при открытии
                      triggerConfetti();
                      // После анимации показываем результат
                      setTimeout(() => {
                        setGiftOpened(true);
                      }, 2000);
                    }}
                    className="w-full sm:w-auto min-w-[200px] bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold text-sm sm:text-base md:text-lg py-3 sm:py-4 md:py-5 px-6 sm:px-8 md:px-10 rounded-lg shadow-lg active:scale-95 transition-transform"
                    size="lg"
                  >
                    ОТКРЫТЬ ПОДАРОК
                  </Button>
                ) : (
                  <div className="w-full text-center text-yellow-400 font-bold text-lg animate-pulse">
                    Открываем...
                  </div>
                )}
              </DialogFooter>
            </>
          ) : (
            <>
              {/* Информация о выигрыше после открытия */}
              <DialogHeader className="px-0 sm:px-2">
                <DialogTitle className="text-base sm:text-xl md:text-2xl lg:text-3xl font-bold text-center text-yellow-400 leading-tight break-words px-2 animate-fade-in">
                  🎉 Поздравляем! 🎉
                </DialogTitle>
              </DialogHeader>
              <DialogDescription className="text-center text-white mt-3 sm:mt-4 md:mt-6 px-2 animate-fade-in">
                <div className="flex flex-col items-center gap-4 sm:gap-6">
                  <div className="text-6xl md:text-7xl animate-bounce">
                    {result?.id === 8 ? '🏆' : '🎁'}
                  </div>
                  <div className="space-y-2">
                    <p className="text-lg sm:text-xl md:text-2xl font-semibold text-yellow-300">
                      Вы выиграли:
                    </p>
                    <p 
                      className="text-2xl sm:text-3xl md:text-4xl font-extrabold"
                      style={{ color: result?.bgColor || '#14B8A6' }}
                    >
                      Двухнедельный тур на двоих в Китай
                    </p>
                  </div>
                  <div className="relative w-32 h-32 sm:w-40 sm:h-40 flex items-center justify-center">
                    <img 
                      src={chinaImage} 
                      alt="Тур в Китай" 
                      className="w-full h-full object-cover rounded-xl border-4 border-white/50 shadow-2xl"
                    />
                  </div>
                  <p className="text-gray-300 text-sm sm:text-base mt-4">
                    Для получения приза нажмите кнопку "Забрать приз"
                  </p>
                </div>
              </DialogDescription>
              <DialogFooter className="flex-col sm:flex-row justify-center mt-4 sm:mt-6 md:mt-8 gap-3 sm:gap-4 px-0 sm:px-2">
                <Button
                  onClick={() => {
                    setShowSuperPrizeModal(false);
                    setGiftOpened(false);
                    setIsOpeningGift(false);
                    navigate('/claim-prize');
                  }}
                  className="w-full sm:w-auto min-w-[200px] bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold text-sm sm:text-base md:text-lg py-3 sm:py-4 md:py-5 px-6 sm:px-8 md:px-10 rounded-lg shadow-lg active:scale-95 transition-transform"
                  size="lg"
                >
                  ЗАБРАТЬ ПРИЗ
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LotteryWheel;
